import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ExternalLink, RefreshCw, CheckSquare } from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { listAufgaben, patchTrackerTask } from "@/lib/api";
import { germanDate } from "@/lib/format";
import { PRIO_BY_VALUE, TASK_PRIORITIES } from "@/lib/tracker";
import { useTableSort, SortHeader } from "@/components/SortHeader";
import { SearchBox, useFulltextFilter, MatchCount } from "@/components/SearchBox";
import SuchAuswahl from "@/components/ui/such-auswahl";
import { useSchreibschutz } from "@/lib/auth";
import { Seitenkopf } from "@/hilfe/Seitenkopf";
import { sperre } from "@/hilfe/Knopfhilfe";

// Aufgaben liegen an der Einheit. Wer wissen will, was insgesamt ansteht,
// musste bisher jede Wohnung einzeln aufklappen -- diese Seite dreht das um:
// alle Aufgaben in einer Tabelle, nach jeder Spalte sortierbar.

// Fuer die Sortierung: hoch vor normal vor niedrig, nicht alphabetisch.
const PRIO_RANG = { hoch: 0, normal: 1, niedrig: 2 };

const Aufgaben = () => {
    const [daten, setDaten] = useState(null);
    const [laedt, setLaedt] = useState(true);
    const [zeigen, setZeigen] = useState("offen");      // offen | alle | erledigt
    const [busy, setBusy] = useState(null);
    const { gesperrt, grund } = useSchreibschutz();

    const laden = async () => {
        setLaedt(true);
        try {
            setDaten(await listAufgaben());
        } catch (e) {
            toast.error("Aufgaben nicht geladen", { description: e?.response?.data?.detail });
        } finally { setLaedt(false); }
    };
    useEffect(() => { laden(); }, []);

    const alle = daten?.aufgaben || [];
    const gefiltert = useMemo(() => alle.filter((a) => (
        zeigen === "alle" ? true : zeigen === "erledigt" ? a.is_completed : !a.is_completed
    )), [alle, zeigen]);

    const searchFields = useMemo(() => [
        "title", "assigned_to", "notes", "unit_label", "property_address", "entity_name", "tenant_name",
    ], []);
    const { query, setQuery, filtered, total, matches, active } = useFulltextFilter(gefiltert, searchFields);

    // Was der Mensch sieht, ist die Beschriftung -- danach wird auch sortiert.
    const resolvers = useMemo(() => ({
        priority: (a) => PRIO_RANG[a.priority] ?? 1,
        updated_at: (a) => a.updated_at || a.created_at || "",
        assigned_to: (a) => a.assigned_to || "",
        entity_name: (a) => a.entity_name || "",
        property_address: (a) => a.property_address || "",
        unit_label: (a) => a.unit_label || "",
        tenant_name: (a) => a.tenant_name || "",
        created_at: (a) => a.created_at || "",
        due_date: (a) => a.due_date || "",
    }), []);
    const { sortKey, sortDir, toggle, sorted } = useTableSort(filtered, "priority", "asc", resolvers);

    const umschalten = async (a) => {
        setBusy(a.id);
        try {
            await patchTrackerTask(a.unit_id, a.id, { is_completed: !a.is_completed });
            await laden();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Aktualisierung fehlgeschlagen");
        } finally { setBusy(null); }
    };

    return (
        <div className="px-6 md:px-10 py-8 space-y-6" data-testid="aufgaben">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Prozesse</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="aufgaben-titel">Aufgaben</h1>
                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                        Alles, was an Einheiten offen ist — über den ganzen Bestand. Jede Spalte ist sortierbar;
                        der Haken schließt eine Aufgabe ab.
                    </p>
                    <Seitenkopf id="seite.aufgaben" />
                </div>
                <Button variant="outline" size="sm" onClick={laden} disabled={laedt} grund="Wird geladen" data-testid="aufgaben-neu-laden">
                    <RefreshCw size={13} className={`mr-1.5 ${laedt ? "animate-spin" : ""}`} /> Neu laden
                </Button>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <SuchAuswahl wert={zeigen} onWaehlen={setZeigen}
                    optionen={[
                        { wert: "offen", text: "Nur offene" },
                        { wert: "erledigt", text: "Nur erledigte" },
                        { wert: "alle", text: "Alle" },
                    ]}
                    className="w-[170px]"
                    testId="aufgaben-filter"
                />
                <SearchBox value={query} onChange={setQuery} placeholder="Suche Aufgabe, Bearbeiter, Objekt, Mieter…"
                           testid="aufgaben-suche" className="w-[320px]" />
                <MatchCount active={active} matches={matches} total={total} />
                {daten && (
                    <div className="flex items-center gap-3 text-xs text-gray-500 ml-auto" data-testid="aufgaben-zaehler">
                        <span><b className="text-slate-900">{daten.offen}</b> offen</span>
                        <span>·</span>
                        <span>{daten.anzahl} gesamt</span>
                        {daten.ueberfaellig > 0 && (<><span>·</span>
                            <span className="text-red-700 font-medium">{daten.ueberfaellig} überfällig</span></>)}
                    </div>
                )}
            </div>

            <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-[0.12em] text-gray-500">
                        <tr>
                            <th className="px-3 py-3 w-8"></th>
                            <SortHeader label="Prio" sortKey="priority" state={{ sortKey, sortDir }} onToggle={toggle} />
                            <th className="text-left font-medium px-4 py-3">Aufgabe</th>
                            <SortHeader label="Fällig" sortKey="due_date" state={{ sortKey, sortDir }} onToggle={toggle} />
                            <SortHeader label="Zuletzt bearbeitet" sortKey="updated_at" state={{ sortKey, sortDir }} onToggle={toggle} />
                            <SortHeader label="Bearbeiter" sortKey="assigned_to" state={{ sortKey, sortDir }} onToggle={toggle} />
                            <SortHeader label="Entität" sortKey="entity_name" state={{ sortKey, sortDir }} onToggle={toggle} />
                            <SortHeader label="Immobilie" sortKey="property_address" state={{ sortKey, sortDir }} onToggle={toggle} />
                            <SortHeader label="Mieteinheit" sortKey="unit_label" state={{ sortKey, sortDir }} onToggle={toggle} />
                            <SortHeader label="Mieter" sortKey="tenant_name" state={{ sortKey, sortDir }} onToggle={toggle} />
                            <SortHeader label="Erstellt" sortKey="created_at" state={{ sortKey, sortDir }} onToggle={toggle} />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {sorted.map((a) => {
                            const prio = PRIO_BY_VALUE[a.priority] || PRIO_BY_VALUE.normal;
                            return (
                                <tr key={a.id}
                                    className={`hover:bg-gray-50 ${a.is_completed ? "opacity-50" : a.ueberfaellig ? "bg-red-50/40" : ""}`}
                                    data-testid={`aufgabe-${a.id}`}>
                                    <td className="px-3 py-3">
                                        <Checkbox checked={!!a.is_completed} disabled={gesperrt || busy === a.id}
                                                  onCheckedChange={() => umschalten(a)}
                                                  grund={sperre([gesperrt, grund], [busy === a.id, "Wird gespeichert"])}
                                                  title="Aufgabe abschließen"
                                                  data-testid={`aufgabe-haken-${a.id}`} />
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <span className={`inline-flex items-center gap-1.5 text-xs ${prio.color}`}>
                                            <span className={`w-1.5 h-1.5 rounded-full ${prio.dot}`} />
                                            {prio.label}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className={`text-slate-900 ${a.is_completed ? "line-through" : ""}`}>{a.title}</div>
                                        {(a.notes || a.link) && (
                                            <div className="text-[11px] text-gray-500 flex items-center gap-2 mt-0.5">
                                                {a.notes && <span>{a.notes}</span>}
                                                {a.link && (
                                                    <a href={a.link} target="_blank" rel="noreferrer"
                                                       className="inline-flex items-center gap-0.5 text-sky-700 hover:underline">
                                                        <ExternalLink size={9} /> Link
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                                        {a.due_date ? (
                                            <span className={a.ueberfaellig ? "text-red-700 font-medium inline-flex items-center gap-1" : "text-gray-600"}>
                                                {a.ueberfaellig && <AlertTriangle size={11} />}
                                                {germanDate(a.due_date)}
                                            </span>
                                        ) : <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                                        {a.updated_at ? germanDate(a.updated_at)
                                            : <span className="text-gray-300" title="Seit dem Anlegen nicht bearbeitet">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-700">
                                        {a.assigned_to || <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-600">{a.entity_name || "—"}</td>
                                    <td className="px-4 py-3 text-xs">
                                        {a.property_id ? (
                                            <Link to={`/immobilien/${a.property_id}`} className="text-gray-700 hover:underline">
                                                {a.property_address || "—"}
                                            </Link>
                                        ) : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        <Link to={`/einheiten/${a.unit_id}`} className="text-gray-700 hover:underline">
                                            {a.unit_label || "Einheit"}
                                        </Link>
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {a.tenant_id ? (
                                            <Link to={`/mieter?edit=${a.tenant_id}`} className="text-gray-700 hover:underline">
                                                {a.tenant_name}
                                            </Link>
                                        ) : <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{germanDate(a.created_at)}</td>
                                </tr>
                            );
                        })}
                        {!sorted.length && (
                            <tr><td colSpan={11} className="px-4 py-12 text-center text-sm text-gray-400">
                                {laedt ? "Lädt …"
                                    : zeigen === "offen" ? "Keine offene Aufgabe — alles erledigt."
                                    : "Keine Aufgabe gefunden."}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="text-[11px] text-gray-400 flex items-center gap-1.5">
                <CheckSquare size={11} />
                Aufgaben entstehen an der Einheit — in der Einheitenliste über „+ Aufgabe", in der
                Einheitenansicht oder per Telegram.
            </div>
        </div>
    );
};

export default Aufgaben;
